import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  LoaderCircle,
  RefreshCw,
  Share2,
  Trophy,
  UsersRound,
  createIcons,
} from "lucide";
import { CHOICE_LABELS, QUESTIONS } from "../shared/questions";
import { formatScore } from "../shared/scoring";
import type {
  LeaderboardEntry,
  LeaderboardResponse,
  SubmitResponse,
} from "../shared/types";
import { ApiError, getLeaderboard, submitAnswers } from "./api";
import "./styles.css";

type View = "home" | "quiz" | "confirm" | "result" | "leaderboard";

interface Draft {
  nickname: string;
  answers: Array<number | null>;
  questionIndex: number;
}

interface AppState extends Draft {
  view: View;
  returnView: View;
  leaderboard: LeaderboardResponse;
  leaderboardLoading: boolean;
  leaderboardError: string;
  homeError: string;
  submitError: string;
  submitting: boolean;
  result: LeaderboardEntry | null;
  shareNotice: string;
}

const DRAFT_KEY = "pj-rank-draft";
const LAST_RESULT_KEY = "pj-rank-last-result";
function getAppRoot(): HTMLDivElement {
  const root = document.querySelector<HTMLDivElement>("#app");
  if (!root) {
    throw new Error("App root was not found.");
  }
  return root;
}
const app = getAppRoot();

function emptyAnswers(): Array<number | null> {
  return Array.from({ length: QUESTIONS.length }, () => null);
}

function restoreDraft(): Draft {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) throw new Error("No draft");
    const parsed = JSON.parse(raw) as Partial<Draft>;
    const answers = Array.isArray(parsed.answers)
      ? parsed.answers.map((answer) =>
          Number.isInteger(answer) && Number(answer) >= 0 && Number(answer) <= 4
            ? Number(answer)
            : null,
        )
      : [];
    if (answers.length !== QUESTIONS.length) throw new Error("Invalid answers");
    return {
      nickname: typeof parsed.nickname === "string" ? parsed.nickname : "",
      answers,
      questionIndex: Math.min(
        Math.max(Number(parsed.questionIndex) || 0, 0),
        QUESTIONS.length - 1,
      ),
    };
  } catch {
    return { nickname: "", answers: emptyAnswers(), questionIndex: 0 };
  }
}

const draft = restoreDraft();
const state: AppState = {
  ...draft,
  view: "home",
  returnView: "home",
  leaderboard: { total: 0, entries: [] },
  leaderboardLoading: true,
  leaderboardError: "",
  homeError: "",
  submitError: "",
  submitting: false,
  result: null,
  shareNotice: "",
};

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>'"]/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        "'": "&#39;",
        '"': "&quot;",
      })[character] ?? character,
  );
}

function persistDraft(): void {
  sessionStorage.setItem(
    DRAFT_KEY,
    JSON.stringify({
      nickname: state.nickname,
      answers: state.answers,
      questionIndex: state.questionIndex,
    } satisfies Draft),
  );
}

function clearDraft(): void {
  sessionStorage.removeItem(DRAFT_KEY);
}

function resetForNextPerson(): void {
  state.nickname = "";
  state.answers = emptyAnswers();
  state.questionIndex = 0;
  state.result = null;
  state.homeError = "";
  state.submitError = "";
  clearDraft();
}

function answersComplete(): boolean {
  return state.answers.every((answer) => answer !== null);
}

function scoreTone(score: number): "p" | "middle" | "j" {
  if (score <= -20) return "p";
  if (score >= 20) return "j";
  return "middle";
}

function renderIconButton(
  icon: string,
  label: string,
  action: string,
): string {
  return `<button class="icon-button" type="button" data-action="${action}" aria-label="${label}" title="${label}"><i data-lucide="${icon}" aria-hidden="true"></i></button>`;
}

function renderHeader(): string {
  return `
    <header class="site-header">
      <div class="header-inner">
        <button class="wordmark" type="button" data-action="home" aria-label="返回首页">
          <span class="wordmark-p">P</span><span class="wordmark-slash">/</span><span class="wordmark-j">J</span>
          <span>排排站</span>
        </button>
        <div class="header-actions">
          ${renderIconButton("share-2", "分享链接", "share")}
          ${renderIconButton("trophy", "查看排行榜", "leaderboard")}
        </div>
      </div>
      <div class="pj-stripe" aria-hidden="true"><span></span><span></span><span></span></div>
    </header>
  `;
}

function renderStatus(): string {
  if (!state.shareNotice) return "";
  return `<div class="toast" role="status">${escapeHtml(state.shareNotice)}</div>`;
}

function renderShell(content: string): string {
  return `
    ${renderHeader()}
    <main id="main-content">${content}</main>
    <footer class="site-footer">
      <p>群聊娱乐测试，不代表正式 MBTI 结果。</p>
    </footer>
    ${renderStatus()}
  `;
}

function renderLoadingRows(count = 3): string {
  return Array.from(
    { length: count },
    () => `
      <tr class="skeleton-row" aria-hidden="true">
        <td><span class="skeleton skeleton-rank"></span></td>
        <td><span class="skeleton skeleton-name"></span></td>
        <td><span class="skeleton skeleton-track"></span></td>
        <td><span class="skeleton skeleton-score"></span></td>
      </tr>
    `,
  ).join("");
}

function renderSpectrum(score: number, compact = false): string {
  const tone = scoreTone(score);
  const readable = `${score <= -20 ? "偏 P" : score >= 20 ? "偏 J" : "弹性区"}，${formatScore(score)}`;
  return `
    <div class="spectrum ${compact ? "spectrum-compact" : ""}">
      <div class="spectrum-labels" aria-hidden="true">
        <span class="label-p">P</span>
        ${compact ? "" : '<span class="label-middle">弹性区</span>'}
        <span class="label-j">J</span>
      </div>
      <input
        class="spectrum-range tone-${tone}"
        type="range"
        min="-100"
        max="100"
        value="${score}"
        tabindex="-1"
        aria-label="${readable}"
        disabled
      />
      ${
        compact
          ? ""
          : '<div class="spectrum-ticks" aria-hidden="true"><span>-100</span><span>-20</span><span>20</span><span>+100</span></div>'
      }
    </div>
  `;
}

function renderLeaderboardTable(
  entries: readonly LeaderboardEntry[],
  currentEntry: LeaderboardEntry | null,
): string {
  const currentKey = currentEntry
    ? `${currentEntry.nickname}|${currentEntry.submittedAt}`
    : "";

  const rows = entries
    .map((entry) => {
      const key = `${entry.nickname}|${entry.submittedAt}`;
      const isCurrent = key === currentKey;
      return `
        <tr class="leader-row ${isCurrent ? "leader-row-current" : ""}" data-testid="leaderboard-row">
          <td class="rank-cell"><span class="rank-number">${entry.rank}</span></td>
          <td class="name-cell">
            <span class="name-line" title="${escapeHtml(entry.nickname)}">
              <span class="nickname">${escapeHtml(entry.nickname)}</span>
              ${isCurrent ? '<span class="you-badge">你</span>' : ""}
            </span>
            <span class="tendency tone-text-${scoreTone(entry.score)}">${entry.tendency}</span>
          </td>
          <td class="spectrum-cell">${renderSpectrum(entry.score, true)}</td>
          <td class="score-cell">${formatScore(entry.score)}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <div class="table-wrap">
      <table class="leaderboard-table">
        <colgroup>
          <col class="col-rank" />
          <col class="col-name" />
          <col class="col-spectrum" />
          <col class="col-score" />
        </colgroup>
        <thead>
          <tr><th>名次</th><th>昵称</th><th>光谱</th><th>分数</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `;
}

function renderLeaderboardContent(
  entries: readonly LeaderboardEntry[],
  currentEntry: LeaderboardEntry | null,
  preview = false,
): string {
  if (state.leaderboardLoading) {
    return `
      <div class="table-wrap" aria-label="排行榜加载中">
        <table class="leaderboard-table"><tbody>${renderLoadingRows()}</tbody></table>
      </div>
    `;
  }

  if (state.leaderboardError) {
    return `
      <div class="empty-state" role="alert">
        <p>${escapeHtml(state.leaderboardError)}</p>
        <button class="button button-secondary" type="button" data-action="retry-leaderboard">
          <i data-lucide="refresh-cw" aria-hidden="true"></i><span>重新加载</span>
        </button>
      </div>
    `;
  }

  if (entries.length === 0) {
    return `
      <div class="empty-state">
        <i data-lucide="users-round" aria-hidden="true"></i>
        <p>还没有人上榜</p>
        <span>第一个位置留给你。</span>
      </div>
    `;
  }

  const visibleEntries = preview ? entries.slice(0, 5) : entries;
  return `
    ${renderLeaderboardTable(visibleEntries, currentEntry)}
    ${
      preview && entries.length > 5
        ? `<button class="text-button" type="button" data-action="leaderboard">查看全部 ${entries.length} 人 <i data-lucide="arrow-right" aria-hidden="true"></i></button>`
        : ""
    }
  `;
}

function renderHome(): string {
  const completedDraft = answersComplete() && state.nickname.trim().length > 0;
  return renderShell(`
    <section class="home-start">
      <div class="home-intro">
        <p class="eyebrow">群友 P/J 光谱</p>
        <h1 class="display-title view-title" tabindex="-1">你会提前定下来，<br />还是边走边看？</h1>
        <p class="home-count"><i data-lucide="users-round" aria-hidden="true"></i><span>${state.leaderboard.total} 人已上榜</span></p>
      </div>
      <form class="start-form" data-form="start" novalidate>
        <label for="nickname">群昵称</label>
        <input
          id="nickname"
          name="nickname"
          data-testid="nickname-input"
          type="text"
          maxlength="24"
          autocomplete="nickname"
          placeholder="输入大家认识的昵称"
          value="${escapeHtml(state.nickname)}"
          aria-describedby="nickname-help ${state.homeError ? "nickname-error" : ""}"
          ${state.homeError ? 'aria-invalid="true"' : ""}
        />
        <p id="nickname-help" class="field-help">1–12 个字符，提交后不可修改</p>
        ${
          state.homeError
            ? `<p id="nickname-error" class="field-error" role="alert">${escapeHtml(state.homeError)}</p>`
            : ""
        }
        <button class="button button-primary button-wide" type="submit" data-testid="start-button">
          <span>${completedDraft ? "继续提交" : "开始答题"}</span><i data-lucide="arrow-right" aria-hidden="true"></i>
        </button>
      </form>
    </section>

    <section class="leaderboard-section" aria-labelledby="home-leaderboard-title">
      <div class="section-heading">
        <div>
          <p class="eyebrow">实时排序</p>
          <h2 id="home-leaderboard-title">群友排行榜</h2>
        </div>
        ${renderIconButton("refresh-cw", "刷新排行榜", "retry-leaderboard")}
      </div>
      ${renderLeaderboardContent(state.leaderboard.entries, state.result, true)}
    </section>
  `);
}

function renderQuiz(): string {
  const question = QUESTIONS[state.questionIndex];
  if (!question) return renderHome();
  const selected = state.answers[state.questionIndex];
  return renderShell(`
    <section class="quiz-shell">
      <div class="quiz-meta">
        <button class="back-link" type="button" data-action="home">
          <i data-lucide="chevron-left" aria-hidden="true"></i><span>${escapeHtml(state.nickname)}</span>
        </button>
        <span>第 ${state.questionIndex + 1} / ${QUESTIONS.length} 题</span>
      </div>
      <progress class="progress-track" aria-label="答题进度" max="${QUESTIONS.length}" value="${state.questionIndex + 1}"></progress>

      <form class="quiz-tool" data-form="question">
        <fieldset>
          <legend class="question-title view-title" tabindex="-1">${escapeHtml(question.scene)}</legend>
          <div class="question-sides" aria-hidden="true">
            <div class="side side-left"><span>左</span><p>${escapeHtml(question.left)}</p></div>
            <div class="side-connector"><span></span></div>
            <div class="side side-right"><span>右</span><p>${escapeHtml(question.right)}</p></div>
          </div>
          <div class="choice-list">
            ${CHOICE_LABELS.map(
              (label, index) => `
                <label class="choice ${selected === index ? "choice-selected" : ""}">
                  <input
                    type="radio"
                    name="answer"
                    value="${index}"
                    data-testid="choice-${index}"
                    ${selected === index ? "checked" : ""}
                  />
                  <span class="choice-dot" aria-hidden="true"></span>
                  <span>${label}</span>
                </label>
              `,
            ).join("")}
          </div>
        </fieldset>

        <div class="quiz-actions">
          <button class="button button-secondary" type="button" data-action="previous" ${state.questionIndex === 0 ? "disabled" : ""}>
            <i data-lucide="arrow-left" aria-hidden="true"></i><span>上一题</span>
          </button>
          <button class="button button-primary" type="submit" data-testid="next-button" ${selected === null ? "disabled" : ""}>
            <span>${state.questionIndex === QUESTIONS.length - 1 ? "查看结果" : "下一题"}</span><i data-lucide="arrow-right" aria-hidden="true"></i>
          </button>
        </div>
      </form>
    </section>
  `);
}

function renderConfirm(): string {
  return renderShell(`
    <section class="confirm-shell">
      <div class="confirm-mark" aria-hidden="true"><i data-lucide="check"></i></div>
      <p class="eyebrow">16 / 16 已完成</p>
      <h1 class="display-title view-title" tabindex="-1">准备好上榜了吗？</h1>
      <div class="confirm-summary">
        <span>群昵称</span>
        <strong>${escapeHtml(state.nickname)}</strong>
      </div>
      <p class="confirm-note">提交后不能修改答案或昵称。</p>
      ${
        state.submitError
          ? `<p class="submit-error" role="alert">${escapeHtml(state.submitError)}</p>`
          : ""
      }
      <div class="confirm-actions">
        <button class="button button-secondary" type="button" data-action="back-to-quiz" ${state.submitting ? "disabled" : ""}>
          <i data-lucide="arrow-left" aria-hidden="true"></i><span>返回检查</span>
        </button>
        <button class="button button-primary" type="button" data-action="submit" data-testid="submit-button" ${state.submitting ? "disabled" : ""}>
          ${
            state.submitting
              ? '<i class="spin" data-lucide="loader-circle" aria-hidden="true"></i><span>正在提交</span>'
              : '<span>确认提交</span><i data-lucide="arrow-right" aria-hidden="true"></i>'
          }
        </button>
      </div>
    </section>
  `);
}

function renderResult(): string {
  if (!state.result) return renderHome();
  return renderShell(`
    <section class="result-band">
      <p class="eyebrow">${escapeHtml(state.result.nickname)} 的位置</p>
      <div class="result-heading">
        <h1 class="display-title view-title" tabindex="-1">${state.result.tendency}</h1>
        <strong class="result-score tone-text-${scoreTone(state.result.score)}" data-testid="result-score">${formatScore(state.result.score)}</strong>
      </div>
      ${renderSpectrum(state.result.score)}
      <div class="result-actions">
        <button class="button button-primary" type="button" data-action="share">
          <i data-lucide="share-2" aria-hidden="true"></i><span>分享给群友</span>
        </button>
        <button class="button button-secondary" type="button" data-action="next-person">
          <span>下一位答题</span><i data-lucide="arrow-right" aria-hidden="true"></i>
        </button>
      </div>
    </section>

    <section class="leaderboard-section" aria-labelledby="result-leaderboard-title">
      <div class="section-heading">
        <div>
          <p class="eyebrow">共 ${state.leaderboard.total} 人</p>
          <h2 id="result-leaderboard-title">群友排行榜</h2>
        </div>
        ${renderIconButton("refresh-cw", "刷新排行榜", "retry-leaderboard")}
      </div>
      ${renderLeaderboardContent(state.leaderboard.entries, state.result)}
    </section>
  `);
}

function renderLeaderboard(): string {
  return renderShell(`
    <section class="standalone-leaderboard leaderboard-section">
      <div class="section-heading section-heading-large">
        <div>
          <button class="back-link" type="button" data-action="return">
            <i data-lucide="chevron-left" aria-hidden="true"></i><span>返回</span>
          </button>
          <p class="eyebrow">共 ${state.leaderboard.total} 人</p>
          <h1 class="display-title view-title" tabindex="-1">群友排行榜</h1>
        </div>
        ${renderIconButton("refresh-cw", "刷新排行榜", "retry-leaderboard")}
      </div>
      ${renderLeaderboardContent(state.leaderboard.entries, state.result)}
    </section>
  `);
}

function render(): void {
  const content =
    state.view === "quiz"
      ? renderQuiz()
      : state.view === "confirm"
        ? renderConfirm()
        : state.view === "result"
          ? renderResult()
          : state.view === "leaderboard"
            ? renderLeaderboard()
            : renderHome();
  app.innerHTML = content;
  createIcons({
    icons: {
      ArrowLeft,
      ArrowRight,
      Check,
      ChevronLeft,
      LoaderCircle,
      RefreshCw,
      Share2,
      Trophy,
      UsersRound,
    },
    attrs: { "stroke-width": 1.8 },
  });
}

function focusView(): void {
  requestAnimationFrame(() => {
    document.querySelector<HTMLElement>(".view-title")?.focus({ preventScroll: true });
  });
}

function setView(view: View, focus = true): void {
  state.view = view;
  window.scrollTo({ top: 0, behavior: "instant" });
  render();
  if (focus) focusView();
}

async function refreshLeaderboard(): Promise<void> {
  state.leaderboardLoading = true;
  state.leaderboardError = "";
  render();
  try {
    state.leaderboard = await getLeaderboard();
  } catch (caught) {
    state.leaderboardError =
      caught instanceof Error ? caught.message : "排行榜暂时加载失败";
  } finally {
    state.leaderboardLoading = false;
    render();
  }
}

function validateNickname(value: string): string {
  const normalized = value.normalize("NFKC").trim().replace(/\s+/gu, " ");
  if (Array.from(normalized).length === 0) return "请输入群昵称";
  if (Array.from(normalized).length > 12) return "昵称最多 12 个字符";
  return "";
}

async function submitCurrentAnswers(): Promise<void> {
  const answers = state.answers.filter((answer): answer is number => answer !== null);
  if (answers.length !== QUESTIONS.length || state.submitting) return;

  state.submitting = true;
  state.submitError = "";
  render();

  try {
    const response: SubmitResponse = await submitAnswers(state.nickname, answers);
    state.result = response.result;
    state.leaderboard = response.leaderboard;
    state.leaderboardLoading = false;
    localStorage.setItem(
      LAST_RESULT_KEY,
      JSON.stringify({
        nickname: response.result.nickname,
        submittedAt: response.result.submittedAt,
      }),
    );
    clearDraft();
    setView("result");
  } catch (caught) {
    if (caught instanceof ApiError && caught.code === "NICKNAME_TAKEN") {
      state.homeError = caught.message;
      setView("home");
      requestAnimationFrame(() => {
        document.querySelector<HTMLInputElement>("#nickname")?.select();
      });
    } else {
      state.submitError =
        caught instanceof Error ? caught.message : "暂时无法提交，请稍后重试";
      render();
    }
  } finally {
    state.submitting = false;
  }
}

async function sharePage(): Promise<void> {
  const shareData = {
    title: "P/J 排排站",
    text: "你会提前定下来，还是边走边看？来群友 P/J 光谱上找位置。",
    url: window.location.origin,
  };
  try {
    if (navigator.share) {
      await navigator.share(shareData);
      return;
    }
    await navigator.clipboard.writeText(shareData.url);
    state.shareNotice = "链接已复制";
  } catch (caught) {
    if (caught instanceof DOMException && caught.name === "AbortError") return;
    state.shareNotice = "复制失败，请从地址栏复制链接";
  }
  render();
  window.setTimeout(() => {
    state.shareNotice = "";
    render();
  }, 2200);
}

app.addEventListener("submit", (event) => {
  event.preventDefault();
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  if (form.dataset.form === "start") {
    const input = form.elements.namedItem("nickname");
    if (!(input instanceof HTMLInputElement)) return;
    state.nickname = input.value.normalize("NFKC").trim().replace(/\s+/gu, " ");
    state.homeError = validateNickname(state.nickname);
    if (state.homeError) {
      render();
      requestAnimationFrame(() =>
        document.querySelector<HTMLInputElement>("#nickname")?.focus(),
      );
      return;
    }
    persistDraft();
    setView(answersComplete() ? "confirm" : "quiz");
  }

  if (form.dataset.form === "question") {
    if (state.answers[state.questionIndex] === null) return;
    if (state.questionIndex === QUESTIONS.length - 1) {
      setView("confirm");
    } else {
      state.questionIndex += 1;
      persistDraft();
      setView("quiz");
    }
  }
});

app.addEventListener("change", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.name !== "answer") return;
  state.answers[state.questionIndex] = Number(target.value);
  persistDraft();
  render();
});

app.addEventListener("input", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.name !== "nickname") return;
  state.nickname = target.value;
  if (state.homeError) {
    state.homeError = "";
    target.removeAttribute("aria-invalid");
    document.querySelector("#nickname-error")?.remove();
  }
});

app.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest<HTMLButtonElement>("[data-action]");
  if (!button || button.disabled) return;
  const action = button.dataset.action;

  if (action === "home") {
    if (state.view === "result") resetForNextPerson();
    setView("home");
  } else if (action === "leaderboard") {
    state.returnView = state.view;
    setView("leaderboard");
  } else if (action === "return") {
    setView(state.returnView === "leaderboard" ? "home" : state.returnView);
  } else if (action === "share") {
    void sharePage();
  } else if (action === "retry-leaderboard") {
    void refreshLeaderboard();
  } else if (action === "previous") {
    state.questionIndex = Math.max(0, state.questionIndex - 1);
    persistDraft();
    setView("quiz");
  } else if (action === "back-to-quiz") {
    state.questionIndex = QUESTIONS.length - 1;
    setView("quiz");
  } else if (action === "submit") {
    void submitCurrentAnswers();
  } else if (action === "next-person") {
    resetForNextPerson();
    setView("home");
    requestAnimationFrame(() =>
      document.querySelector<HTMLInputElement>("#nickname")?.focus(),
    );
  }
});

render();
void refreshLeaderboard();
